const UNSUPPORTED_MESSAGE_TYPES = new Set(['unsupported', 'errors', 'unknown']);

export const UNAVAILABLE_MESSAGE_CODE = 131060;

export const UNSUPPORTED_MESSAGE_PREVIEW = '⚠️ Unsupported message';
export const UNAVAILABLE_MESSAGE_PREVIEW = '⚠️ Message unavailable';

const SUBTYPE_LABELS: Record<string, string> = {
  poll_creation: 'Poll',
  poll_update: 'Poll vote',
  edit: 'Message edit',
  gif: 'GIF',
  group_invite: 'Group invite',
  keep_in_chat: 'Kept message',
  pin: 'Pinned message',
  media_placeholder: 'Media message',
  link_preview: 'Link preview',
  hsm: 'Template message',
  interactive: 'Interactive message',
  button: 'Button reply',
  list: 'List reply',
  product: 'Product message',
  order: 'Order',
  reaction: 'Reaction',
  location: 'Location',
  image: 'Image',
};

export interface UnsupportedMessageInfo {
  code: number | null;
  subtype: string | null;
  subtypeLabel: string | null;
  unavailable: boolean;
  title: string;
  body: string;
  preview: string;
}

interface UnsupportedMessageLike {
  message_type?: string | null;
  messageType?: string | null;
  metadata?: unknown;
  is_inbound?: unknown;
  content_text?: unknown;
}

export function isUnsupportedMessageType(type: unknown): boolean {
  return typeof type === 'string' && UNSUPPORTED_MESSAGE_TYPES.has(type.trim().toLowerCase());
}

function readMetadata(metadata: unknown): Record<string, unknown> | null {
  let value = metadata;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function readCode(metadata: Record<string, unknown> | null): number | null {
  const errors = metadata?.errors;
  if (!Array.isArray(errors)) return null;
  const first = errors.find((e) => e && typeof e === 'object') as Record<string, unknown> | undefined;
  if (!first || first.code == null) return null;
  const code = Number(first.code);
  return Number.isFinite(code) ? code : null;
}

function readSubtype(metadata: Record<string, unknown> | null): string | null {
  const unsupported = metadata?.unsupported;
  if (!unsupported || typeof unsupported !== 'object') return null;
  const record = unsupported as Record<string, unknown>;
  for (const value of [record.type, record.raw_type]) {
    if (typeof value === 'string' && value.trim()) return value.trim().toLowerCase();
  }
  return null;
}

export function unsupportedSubtypeLabel(subtype: string | null | undefined): string | null {
  const key = subtype?.trim().toLowerCase();
  if (!key || key === 'errors') return null;
  if (SUBTYPE_LABELS[key]) return SUBTYPE_LABELS[key];
  const spaced = key.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function previewFor(code: number | null, subtypeLabel: string | null): string {
  if (code === UNAVAILABLE_MESSAGE_CODE) return UNAVAILABLE_MESSAGE_PREVIEW;
  if (subtypeLabel) return `⚠️ ${subtypeLabel} (not supported)`;
  return UNSUPPORTED_MESSAGE_PREVIEW;
}

export function getUnsupportedMessageInfo(message: UnsupportedMessageLike | null | undefined): UnsupportedMessageInfo | null {
  if (!message || !isUnsupportedMessageType(message.message_type ?? message.messageType)) return null;

  const metadata = readMetadata(message.metadata);
  const code = readCode(metadata);
  const subtype = readSubtype(metadata);
  const subtypeLabel = unsupportedSubtypeLabel(subtype);
  const unavailable = code === UNAVAILABLE_MESSAGE_CODE;
  const isInbound = message.is_inbound == null || Boolean(message.is_inbound) || message.is_inbound === '1';
  const preview = previewFor(code, subtypeLabel);
  const base = { code, subtype, subtypeLabel, unavailable, preview };

  if (!isInbound) {
    return {
      ...base,
      title: subtypeLabel ? `${subtypeLabel} not supported` : 'Unsupported message',
      body: "Sent from the WhatsApp Business app. WhatsApp Cloud API doesn't support this message type, so its content isn't available here.",
    };
  }

  if (unavailable) {
    return {
      ...base,
      title: 'Message unavailable',
      body: 'WhatsApp reported this message as currently unavailable (error 131060), so its content isn\'t available here.',
    };
  }

  if (subtype === 'edit') {
    return {
      ...base,
      title: 'Message edited',
      body: "The customer edited a message. WhatsApp Cloud API currently delivers edits without their content, so the edited text isn't available here.",
    };
  }

  if (subtypeLabel) {
    const noun = subtypeLabel === 'GIF' ? 'GIF' : subtypeLabel.toLowerCase();
    const article = /^[aeiou]/i.test(noun) ? 'an' : 'a';
    return {
      ...base,
      title: `${subtypeLabel} not supported`,
      body: `The customer sent ${article} ${noun}. WhatsApp Cloud API doesn't support this message type, so its content isn't available here.`,
    };
  }

  return {
    ...base,
    title: 'Unsupported message',
    body: "The customer sent a message type WhatsApp Cloud API doesn't support, and WhatsApp didn't say which type it was. Its content isn't available here.",
  };
}

export function getUnsupportedMessagePreview(message: UnsupportedMessageLike | null | undefined): string | null {
  if (!message || !isUnsupportedMessageType(message.message_type ?? message.messageType)) return null;
  const metadata = readMetadata(message.metadata);
  const code = readCode(metadata);
  const subtypeLabel = unsupportedSubtypeLabel(readSubtype(metadata));
  if (code != null || subtypeLabel) return previewFor(code, subtypeLabel);
  const stored = typeof message.content_text === 'string' ? message.content_text.trim() : '';
  return stored.startsWith('⚠️') ? stored : UNSUPPORTED_MESSAGE_PREVIEW;
}
